// Internationalization translations
// To add a new language, add a new key to the translations object

import { en } from './en'
import { es } from './es'
import { de } from './de'
import { fr } from './fr'
import { pt } from './pt'
import { pl } from './pl'
import { nl } from './nl'
import { it } from './it'
import { ja } from './ja'
import { ko } from './ko'

export type Language = 'en' | 'es' | 'de' | 'fr' | 'pt' | 'pl' | 'nl' | 'it' | 'ja' | 'ko'

export interface Translations {
  // Header
  title: string
  subtitle: string
  settings: string

  // Resources
  resources: string
  money: string
  energy: string
  compute: string
  quality: string
  totalUsers: string
  price: string
  research: string

  // Actions
  train: string
  trainCost: string
  qualityGain: string
  inference: string
  needUsers: string
  usersWaiting: string
  qualityToAttract: string
  researchAction: string
  unlocksAfter: string
  computeSpent: string
  cost: string
  compShort: string
  energyShort: string

  // Hardware
  hardware: string
  computePerSec: string
  usesEnergy: string
  devicesTotal: string

  // Models
  training: string
  yourModels: string
  params: string
  maxUsers: string
  trainCost2: string
  unlocked: string
  researchPoints: string

  // Upgrades
  upgrades: string
  upgradesLockedMsg: string
  max: string
  all: string
  
  // Upgrade tiers
  tierGarage: string
  tierGarageDesc: string
  tierStartup: string
  tierStartupDesc: string
  tierScaleUp: string
  tierScaleUpDesc: string
  tierEnterprise: string
  tierEnterpriseDesc: string
  tierHyperscale: string
  tierHyperscaleDesc: string
  tierGlobal: string
  tierGlobalDesc: string
  tierSuperintelligence: string
  tierSuperintelligenceDesc: string
  tierSingularity: string
  tierSingularityDesc: string

  // Footer
  computeSpentStat: string
  time: string
  resetGame: string
  resetConfirm: string
  pause: string
  resume: string

  // Event Log
  eventLog: string
  welcome: string

  // Price
  market: string
  pricePerMillion: string

  // Tags
  tagEnergy: string
  tagCompute: string
  tagHw: string
  tagTrain: string
  tagResearch: string
  tagAuto: string

  // Tutorial messages
  tutorialWelcome: string
  tutorialFirstTrain: string
  tutorialQuality20: string
  tutorialQuality40: string
  tutorialQuality50: string
  tutorialFirstUsers: string
  tutorialUsers10: string
  tutorialMoney100: string
  tutorialMoney1k: string
  tutorialResearchUnlock: string
  tutorialFirstResearch: string
  tutorialGpt2Small: string
  tutorialGpt2Large: string
  tutorialGpt3: string
  tutorialGpt4: string
  tutorialAgi: string
  tutorialHwGaming: string
  tutorialHwServer: string
  tutorialHwDatacenter: string
  tutorialHwSatellite: string
  tutorialHwQuantum: string
  tutorialUsers100: string
  tutorialUsers1k: string
  tutorialUsers100k: string
  tutorialUsers1m: string

  // Model descriptions
  modelTinyDesc: string
  modelGpt2SmallDesc: string
  modelGpt2LargeDesc: string
  modelGpt3Desc: string
  modelGpt4Desc: string
  modelAgiDesc: string
  
  // Model descriptions (new keys for all models)
  modelTransformerDesc: string
  modelGpt1Desc: string
  modelGpt2Desc: string
  modelGpt35Desc: string
  modelGpt41Desc: string
  modelU1Desc: string
  model2UDesc: string
  modelGpt5Desc: string
  modelGpt6Desc: string
  modelGpt7Desc: string
  modelAgi1Desc: string
  modelAgi2Desc: string
  modelAgi3Desc: string
  modelAsiDesc: string

  // Upgrade names and descriptions
  upgradeLaptopOverclock: string
  upgradeLaptopOverclockDesc: string
  upgradeBetterOutlet: string
  upgradeBetterOutletDesc: string
  upgradeSolarPanel: string
  upgradeSolarPanelDesc: string
  upgradePowerGrid: string
  upgradePowerGridDesc: string
  upgradeNuclearPlant: string
  upgradeNuclearPlantDesc: string
  upgradeFusionReactor: string
  upgradeFusionReactorDesc: string
  upgradeDysonSphere: string
  upgradeDysonSphereDesc: string
  upgradeRamUpgrade: string
  upgradeRamUpgradeDesc: string
  upgradeSsdArray: string
  upgradeSsdArrayDesc: string
  upgradeBetterOptimizer: string
  upgradeBetterOptimizerDesc: string
  upgradeResearchPapers: string
  upgradeResearchPapersDesc: string
  upgradeSelfServicePortal: string
  upgradeSelfServicePortalDesc: string
  upgradeApiAccess: string
  upgradeApiAccessDesc: string
  upgradeMobileApp: string
  upgradeMobileAppDesc: string
  upgradeEnterpriseTier: string
  upgradeEnterpriseTierDesc: string
  upgradeGlobalCdn: string
  upgradeGlobalCdnDesc: string
  upgradeBasicChatbot: string
  upgradeBasicChatbotDesc: string
  upgradeBulkApi: string
  upgradeBulkApiDesc: string
  upgradeNeuralEdge: string
  upgradeNeuralEdgeDesc: string
  upgradeQuantumInference: string
  upgradeQuantumInferenceDesc: string
  upgradeOmnipresentAi: string
  upgradeOmnipresentAiDesc: string
  upgradeTrainingScheduler: string
  upgradeTrainingSchedulerDesc: string
  upgradeTrainingPipeline: string
  upgradeTrainingPipelineDesc: string
  upgradeMlOpsPlatform: string
  upgradeMlOpsPlatformDesc: string
  upgradeNeuralArchitectureSearch: string
  upgradeNeuralArchitectureSearchDesc: string
  upgradeSelfImprovingTraining: string
  upgradeSelfImprovingTrainingDesc: string
  upgradeResearchIntern: string
  upgradeResearchInternDesc: string
  upgradeResearchTeam: string
  upgradeResearchTeamDesc: string
  upgradeResearchDivision: string
  upgradeResearchDivisionDesc: string
  upgradeAiResearchLab: string
  upgradeAiResearchLabDesc: string
  upgradeAutonomousResearch: string
  upgradeAutonomousResearchDesc: string
  upgradeAntimatterReactor: string
  upgradeAntimatterReactorDesc: string
  upgradePhDScientists: string
  upgradePhDScientistsDesc: string
  upgradeResearchLab: string
  upgradeResearchLabDesc: string
  upgradeAiResearchAssistant: string
  upgradeAiResearchAssistantDesc: string
  upgradeAiScientist: string
  upgradeAiScientistDesc: string
  upgradeResearchSwarm: string
  upgradeResearchSwarmDesc: string
  upgradeRecursiveResearch: string
  upgradeRecursiveResearchDesc: string
  upgradeSingularityResearch: string
  upgradeSingularityResearchDesc: string

  // Hardware upgrade names and descriptions (for hardware purchase upgrades)
  upgradeHwUsedGpu: string
  upgradeHwUsedGpuDesc: string
  upgradeHwGamingGpu: string
  upgradeHwGamingGpuDesc: string
  upgradeHwProGpu: string
  upgradeHwProGpuDesc: string
  upgradeHwWorkstation: string
  upgradeHwWorkstationDesc: string
  upgradeHwServerRack: string
  upgradeHwServerRackDesc: string
  upgradeHwGpuCluster: string
  upgradeHwGpuClusterDesc: string
  upgradeHwMiniDatacenter: string
  upgradeHwMiniDatacenterDesc: string
  upgradeHwDatacenter: string
  upgradeHwDatacenterDesc: string
  upgradeHwMegaDatacenter: string
  upgradeHwMegaDatacenterDesc: string
  upgradeHwDatacenterNetwork: string
  upgradeHwDatacenterNetworkDesc: string
  upgradeHwSupercomputer: string
  upgradeHwSupercomputerDesc: string
  upgradeHwSatellite: string
  upgradeHwSatelliteDesc: string
  upgradeHwQuantum: string
  upgradeHwQuantumDesc: string
  upgradeHwDysonCompute: string
  upgradeHwDysonComputeDesc: string
  upgradeHwMatrioshka: string
  upgradeHwMatrioshkaDesc: string

  // Hardware upgrade descriptions (legacy)
  hwUsedGpuDesc: string
  hwGamingGpuDesc: string
  hwProGpuDesc: string
  hwWorkstationDesc: string
  hwServerRackDesc: string
  hwGpuClusterDesc: string
  hwMiniDatacenterDesc: string
  hwDatacenterDesc: string
  hwMegaDatacenterDesc: string
  hwDatacenterNetworkDesc: string
  hwSupercomputerDesc: string
   hwSatelliteDesc: string
   hwQuantumDesc: string
   
   // Hardware names
   hwLaptopName: string
   hwUsedGpuName: string
   hwGamingGpuName: string
   hwProGpuName: string
   hwWorkstationName: string
   hwServerRackName: string
   hwGpuClusterName: string
   hwMiniDatacenterName: string
   hwDatacenterName: string
   hwMegaDatacenterName: string
   hwDatacenterNetworkName: string
   hwSupercomputerName: string
   hwSatelliteName: string
   hwQuantumName: string
   hwDysonComputeName: string
   hwMatrioshkaName: string

   // Misc
  purchased: string
  switchedTo: string
  unlockedModel: string
  scalesToUsers: string
  trainItToAttract: string
  unlimited: string
  language: string
  
  // Mobile labels
  mobileResources: string
  mobileModels: string
  mobileHardware: string
  mobileUpgrades: string
  sell: string
  sellFor: string
  discontinueConfirm: string
  autoInference: string
  autoInferenceRate: string
  autoTrain: string
  autoTrainRate: string
  autoResearch: string
  autoResearchRate: string

  // Tooltips
  tooltipTitle: string
  tooltipMoney: string
  tooltipEnergy: string
  tooltipCompute: string
  tooltipResearch: string
  tooltipUsers: string
  tooltipComputeSpent: string
  tooltipPlayTime: string
  tooltipSettings: string

  // Share
  shareButton: string
  shareText: string
  shareCopied: string
  imageCopied: string
  shareChallenge: string
  shareTitle: string
  shareDownload: string
  model: string

  // Feedback
  feedback: string
  feedbackTitle: string
  feedbackPlaceholder: string
  feedbackEmailPlaceholder: string
  feedbackSend: string
  feedbackSending: string
  feedbackSuccess: string
  feedbackError: string
  feedbackRateLimit: string
  feedbackClose: string

  // Console messages
  consoleGameLoaded: string
  consoleGameSaved: string
  consoleFailedToSave: string
  consoleFailedToLoad: string
  consoleFailedToClear: string

  // Victory
  victoryTitle: string
  victorySubtitle: string
  victoryTime: string
  victoryCta: string
  victoryClose: string
}

export const translations: Record<Language, Translations> = {
  en,
  es,
  de,
  fr,
  pt,
  pl,
  nl,
  it,
  ja,
  ko,
}

// Get translation by key
export function t(key: keyof Translations, lang: Language): string {
  return translations[lang][key] || translations.en[key] || key
}
